import { trackById, trackByIndex } from 'src/app/shared/utils/track-by.util';
import { AfterViewInit, Component, EventEmitter, Inject, Injector, Input, OnChanges, OnInit, Optional, Output, SimpleChanges, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { CoreGridComponent } from 'src/app/shared/grid/core-grid/core-grid.component';
import { Sample } from 'src/app/models/sample.model';
import { SampleService } from 'src/app/services/sample.service';
import { ToastService } from 'src/app/services/toast.service';
import { CommonService } from 'src/app/services/common.service';
import { AuthService } from 'src/app/services';
import { GridViewService } from 'src/app/services/grid-view.service';
import { GridSettingsService } from 'src/app/shared/grid/core-grid/grid-settings-flyout/grid-settings.service';
import { RolePermissionObjects } from 'src/app/shared/constants/rolePermissionObjects.constants';
import { ColumnConfig } from 'src/app/shared/grid/core-grid/columnConfig.model';
import nameOf from 'src/app/shared/form/nameOf.helper';
import { IhLabResult } from 'src/app/models/IhLabResult.model';
import { ISearchFieldData } from 'src/app/shared/grid/baseGrid.component';
import { Observable, of } from 'rxjs';
import { getSampleGridIdForLab } from 'src/app/shared/constants/gridIds.constants';
import { MatTableDataSource } from '@angular/material/table';

interface CocSampleRow extends Sample {
    barcodeContent?: string | null;
    agentGroupName?: string;
}

@Component({
  standalone: false,
    selector: 'app-coc-sample-grid',
    templateUrl: './coc-sample-grid.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrls: ['./coc-sample-grid.component.scss'],
})
export class CocSampleGridComponent extends CoreGridComponent<Sample> implements OnInit, AfterViewInit, OnChanges {
    private static readonly builtInColumns = [
        'select',
        nameOf<Sample>(x => x.sampleNumber),
        nameOf<IhLabResult>(x => x.masterAgentName),
        nameOf<IhLabResult>(x => x.agentGroupId),
        nameOf<Sample>(x => x.startDate),
        nameOf<Sample>(x => x.sampleVolume),
        nameOf<Sample>(x => x.runTime),
        nameOf<Sample>(x => x.flowRate),
        nameOf<Sample>(x => x.analyticalMethodName),
    ];

    @Input() labId: string;
    @Input() samples: CocSampleRow[] = [];
    @Input() selectedSamples: CocSampleRow[] = [];
    @Input() showCheckboxes: boolean = true;
    @Input() isFinalStep: boolean = false;

    @Output() selectionChanged = new EventEmitter<CocSampleRow[]>();
    @Output() sampleClicked = new EventEmitter<CocSampleRow>();

    localDataSource = new MatTableDataSource<CocSampleRow>([]);
    private isGridInitialized = false;

    constructor(
        private sampleService: SampleService,
        protected override toastService: ToastService,
        protected override dialog: MatDialog,
        protected override commonService: CommonService,
        authService: AuthService,
        gridViewService: GridViewService,
        gridSettingsService: GridSettingsService,
        protected injector: Injector,
        @Optional() @Inject(MAT_DIALOG_DATA) public override searchFieldData: ISearchFieldData
    ) {
        super(toastService, dialog, commonService, authService, gridViewService, gridSettingsService);
        this.gridService = sampleService;
        this.widgetMode = true;
    }

    protected override get objectId(): string {
        return RolePermissionObjects.Sample;
    }

    override get gridId(): string {
        return this.labId != null ? getSampleGridIdForLab(this.labId) : this.objectId;
    }

    protected override get isGridViewEnabled(): boolean {
        return true;
    }

    protected override get deferGridViewInit(): boolean {
        return true;
    }

    override initializeProperties(): void {
        this.addEntityTitle = 'Sample';
        this.sortActive = nameOf<Sample>(x => x.sampleNumber);
        this.defaultOrderBy = 'asc';
        this.searchPlaceholder = 'Search Samples';
    }

    override initializeColumns(): void {
        this.columnConfigs = [
            new ColumnConfig<Sample>({
                name: nameOf<Sample>(x => x.sampleNumber),
                displayName: 'Sample Number',
                cellTemplate: (sample: Sample) => sample.sampleNumber || '-'
            }),
            new ColumnConfig<Sample>({
                name: nameOf<IhLabResult>(x => x.masterAgentName),
                displayName: 'Agent Name',
                cellTemplate: (sample: Sample) => (sample.ihLabResults || []).map(r => r.masterAgentName || '-').join(', ')
            }),
            new ColumnConfig<Sample>({
                name: nameOf<IhLabResult>(x => x.agentGroupId),
                displayName: 'Agent Group',
                cellTemplate: (sample: Sample) => {
                    const results = sample.ihLabResults || [];
                    if (!results.length) return '-';
                    const uniqueGroupIds = [...new Set(results.map(r => r.agentGroupId))];
                    return (uniqueGroupIds.length === 1 && uniqueGroupIds[0] != null) ? results[0].agentGroupName ?? '-' : '-';
                }
            }),
            new ColumnConfig<Sample>({
                name: nameOf<Sample>(x => x.startDate),
                displayName: 'Sampling Date',
                cellTemplate: (sample: Sample) => sample.startDate ? sample.startDate.toString() : '-'
            }),
            new ColumnConfig<Sample>({
                name: nameOf<Sample>(x => x.sampleVolume),
                displayName: 'Sample Volume (L)',
                cellTemplate: (sample: Sample) => sample.sampleVolume?.toString() || '-'
            }),
            new ColumnConfig<Sample>({
                name: nameOf<Sample>(x => x.runTime),
                displayName: 'Total Time (Minutes)',
                cellTemplate: (sample: Sample) => sample.runTime?.toString() || '-'
            }),
            new ColumnConfig<Sample>({
                name: nameOf<Sample>(x => x.flowRate),
                displayName: 'Flowrate',
                cellTemplate: (sample: Sample) => sample.flowRate?.toString() ?? '-'
            }),
            new ColumnConfig<Sample>({
                name: nameOf<Sample>(x => x.analyticalMethodName),
                displayName: 'Method Reference',
                cellTemplate: (sample: Sample) => sample.analyticalMethodName || '-'
            })
        ];
    }

    override ngOnInit(): void {
        this.initializeProperties();
        this.initializeColumns();
        this.setDisplayedColumns();
        this.localDataSource.data = this.samples;
        this.isGridInitialized = true;
        if (this.labId != null) {
            this.initializeView();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['samples'] && this.samples) {
            this.localDataSource.data = this.samples;
        }
        if (this.isGridInitialized && changes['labId'] && this.labId != null) {
            this.initializeView();
        }
    }

    override ngAfterViewInit(): void {}

    isSelected(row: CocSampleRow): boolean {
        return this.selectedSamples.some(s => s.id === row.id);
    }

    toggleSelection(row: CocSampleRow, checked: boolean): void {
        this.selectedSamples = checked
            ? [...this.selectedSamples, row].filter((s, i, arr) => arr.findIndex(x => x.id === s.id) === i)
            : this.selectedSamples.filter(s => s.id !== row.id);
        this.selectionChanged.emit(this.selectedSamples);
    }

    isAllSelected(): boolean {
        return this.samples.length > 0 && this.selectedSamples.length === this.samples.length;
    }

    toggleAll(checked: boolean): void {
        this.selectedSamples = checked ? [...this.samples] : [];
        this.selectionChanged.emit(this.selectedSamples);
    }

    onSampleClick(sample: CocSampleRow): void {
        this.sampleClicked.emit(sample);
    }

    getCellValue(row: CocSampleRow, columnName: string): string {
        const config = this.columnConfigs.find(c => c.name === columnName);
        return config?.cellTemplate ? config.cellTemplate(row) : String((row as unknown as Record<string, unknown>)[columnName] ?? '-');
    }

    hasBarcodeContent(sample: CocSampleRow): boolean {
        return !!sample.barcodeContent;
    }

    getBarcodeContent(sample: CocSampleRow): string {
        return sample.barcodeContent || '';
    }

    isBuiltInColumn(property: string): boolean {
        return CocSampleGridComponent.builtInColumns.includes(property);
    }

    getDisplayColumns(): string[] {
        const columns: string[] = this.showCheckboxes && !this.isFinalStep ? ['select'] : [];
        if (this.gridViewColumns?.length > 0) {
            columns.push(...this.gridViewColumns
                .filter(col => col.visible && col.property !== 'action')
                .sort((a, b) => a.order - b.order)
                .map(col => col.property));
        } else {
            columns.push(...CocSampleGridComponent.builtInColumns.filter(c => c !== 'select'));
        }
        return columns;
    }

    public trackById = trackById;
    public trackByIndex = trackByIndex;

    override loadData(): void {}

    openEntityDialog(entityId?: string): void {}

    openEntityDeleteDialog(entity: Sample): void {}

    initializeOverviewConfigs(): void {}

    initializeTabs(): { routerLink: string; title: string }[] {
        return [];
    }

    getOverviewData(): Observable<void> {
        return of(void 0);
    }
}
